import { Timestamp } from 'firebase/firestore';
import { FirestoreService } from '@/lib/firestore';
import { COLLECTION_NAMES } from '@/lib/collections';
import type {
  ActivityEvent,
  ActivityEventType,
  ResourceType,
  ActionType,
  FieldChange
} from '../types/activityEvent';

/**
 * Helper function to recursively remove undefined values from an object
 * Firestore doesn't accept undefined values, so we need to filter them out
 */
function removeUndefinedValues<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item)) as unknown as T;
  }
  
  if (typeof obj === 'object' && obj.constructor === Object) {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    }
    return cleaned as T;
  }
  
  return obj;
}

/**
 * Input type for emitting activity events, excluding auto-generated fields
 */
type EmitActivityEventInput = Omit<ActivityEvent, 'id' | 'timestamp'>;

/**
 * Convenience input type for common creation events
 */
interface CreateEventInput {
  domain: string;
  event_type: ActivityEventType;
  actor: {
    user_id: string;
    user_name: string;
    avatar_url?: string;
  };
  subject: {
    resource_type: ResourceType;
    resource_id: string;
    resource_name: string;
  };
  context?: {
    division_id?: string;
    division_name?: string;
    program_id?: string;
    program_name?: string;
    team_id?: string;
    team_name?: string;
  };
  metadata: {
    affected_user_ids: string[];
    affected_workflow_ids: string[];
    affected_employee_ids: string[];
    tags: string[];
    visibility: 'public' | 'team' | 'program' | 'division';
  };
  message?: string;
}

/**
 * Convenience input type for update events with field changes
 */
interface UpdateEventInput extends CreateEventInput {
  fields_changed: FieldChange[];
  previous_state?: Record<string, any>;
  new_state?: Record<string, any>;
}

/**
 * Convenience input type for deletion events
 */
interface DeleteEventInput extends CreateEventInput {
  previous_state?: Record<string, any>;
}

/**
 * Convenience input type for comment events
 */
interface CommentEventInput extends CreateEventInput {
  message: string;
}

/**
 * Input for batch event emission
 */
interface BatchEmitInput {
  events: EmitActivityEventInput[];
  batch_id?: string;
}

/**
 * ActivityEventService - Shared multi-domain service for managing activity feed events
 * 
 * This service provides a centralized way to emit and track activity events
 * across all business domains (Workflow, Goals, Hiring, Billing, CRM, Sales, Intel).
 * 
 * Features:
 * - Domain-aware event tracking with required domain field
 * - Creating activity events with auto-generated timestamps
 * - Convenience methods for common event types
 * - Batch event emission for related changes
 * - Proper error handling and validation
 * - Domain field validation to ensure proper event routing
 * 
 * Phase Coverage:
 * - Phase 1 (Current): Event emission and storage with domain support
 * - Phase 2 (Future): Event querying and filtering
 * - Phase 3 (Future): Subscription management
 * - Phase 4 (Future): Real-time notifications
 * 
 * @example
 * ```typescript
 * // Emit a simple creation event
 * const eventId = await activityEventService.emit({
 *   domain: 'workflow',
 *   event_type: 'workflow_spot.created',
 *   actor: { user_id: 'user123', user_name: 'John Doe' },
 *   subject: { resource_type: 'workflow_spot', resource_id: 'spot123', resource_name: 'New Spot' },
 *   change: { action: 'created' },
 *   metadata: { affected_user_ids: ['user123'], affected_workflow_ids: [], affected_employee_ids: [], tags: ['creation'], visibility: 'team' }
 * });
 * ```
 */
class ActivityEventService {
  private firestoreService: FirestoreService<ActivityEvent>;

  constructor() {
    this.firestoreService = new FirestoreService<ActivityEvent>(
      COLLECTION_NAMES.ACTIVITY_LOG,
      {
        enableAudit: false, // Activity log is itself an audit trail
      }
    );
  }

  /**
   * Core method to emit an activity event
   * 
   * This method takes an activity event (without id and timestamp) and:
   * 1. Validates that the domain field is present
   * 2. Auto-generates a timestamp using Firestore's server timestamp
   * 3. Creates the document in Firestore
   * 4. Returns the generated document ID
   * 
   * @param event - Activity event data without id and timestamp
   * @returns Promise resolving to the created event's document ID
   * 
   * @throws Error if domain field is missing or event creation fails
   * 
   * @example
   * ```typescript
   * const eventId = await activityEventService.emit({
   *   domain: 'workflow',
   *   event_type: 'division.created',
   *   actor: { user_id: 'user123', user_name: 'Jane Smith' },
   *   subject: { resource_type: 'division', resource_id: 'div123', resource_name: 'Marketing' },
   *   change: { action: 'created' },
   *   context: { division_id: 'div123', division_name: 'Marketing' },
   *   metadata: {
   *     affected_user_ids: ['user123'],
   *     affected_workflow_ids: [],
   *     affected_employee_ids: [],
   *     tags: ['division', 'creation'],
   *     visibility: 'division'
   *   }
   * });
   * ```
   */
  async emit(event: EmitActivityEventInput): Promise<string> {
    try {
      // Validate domain field is present
      if (!event.domain || typeof event.domain !== 'string') {
        throw new Error('Domain field is required and must be a string (e.g., "workflow", "goals", "hiring")');
      }

      // Add timestamp to the event
      const eventWithTimestamp = {
        ...event,
        timestamp: Timestamp.now(),
      };

      // Remove undefined values to prevent Firestore errors
      const cleanedEvent = removeUndefinedValues(eventWithTimestamp);

      // Create the document using FirestoreService
      const docId = await this.firestoreService.create(cleanedEvent);

      return docId;
    } catch (error) {
      throw new Error(
        `Failed to emit activity event: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Convenience method for emitting creation events
   * 
   * This method simplifies emitting creation events by automatically
   * setting the action to 'created' and structuring the event properly.
   * 
   * @param input - Creation event input data
   * @returns Promise resolving to the created event's document ID
   * 
   * @example
   * ```typescript
   * const eventId = await activityEventService.emitCreation({
   *   domain: 'workflow',
   *   event_type: 'program.created',
   *   actor: { user_id: 'user123', user_name: 'John Doe' },
   *   subject: { resource_type: 'program', resource_id: 'prog123', resource_name: 'New Program' },
   *   context: { division_id: 'div123', division_name: 'Marketing' },
   *   metadata: {
   *     affected_user_ids: ['user123'],
   *     affected_workflow_ids: [],
   *     affected_employee_ids: [],
   *     tags: ['program', 'creation'],
   *     visibility: 'division'
   *   }
   * });
   * ```
   */
  async emitCreation(input: CreateEventInput): Promise<string> {
    return this.emit({
      ...input,
      change: {
        action: 'created' as ActionType,
      },
      context: input.context || {},
    });
  }

  /**
   * Convenience method for emitting update events
   * 
   * This method simplifies emitting update events with field changes,
   * automatically structuring the change details.
   * 
   * @param input - Update event input data with field changes
   * @returns Promise resolving to the created event's document ID
   * 
   * @example
   * ```typescript
   * const eventId = await activityEventService.emitUpdate({
   *   domain: 'workflow',
   *   event_type: 'workflow_spot.updated',
   *   actor: { user_id: 'user123', user_name: 'Jane Smith' },
   *   subject: { resource_type: 'workflow_spot', resource_id: 'spot123', resource_name: 'Spot Name' },
   *   fields_changed: [
   *     { field_name: 'status', old_value: 'pending', new_value: 'approved', display_name: 'Status' }
   *   ],
   *   metadata: {
   *     affected_user_ids: ['user123'],
   *     affected_workflow_ids: ['wf123'],
   *     affected_employee_ids: [],
   *     tags: ['workflow_spot', 'status_change'],
   *     visibility: 'team'
   *   }
   * });
   * ```
   */
  async emitUpdate(input: UpdateEventInput): Promise<string> {
    // Build change object, filtering out undefined fields
    const change: any = {
      action: 'updated' as ActionType,
      fields_changed: input.fields_changed,
    };
    
    // Only include previous_state and new_state if they are defined
    if (input.previous_state !== undefined) {
      change.previous_state = input.previous_state;
    }
    if (input.new_state !== undefined) {
      change.new_state = input.new_state;
    }
    
    return this.emit({
      ...input,
      change,
      context: input.context || {},
    });
  }

  /**
   * Convenience method for emitting deletion events
   * 
   * This method simplifies emitting deletion events, capturing the
   * state before deletion.
   * 
   * @param input - Deletion event input data
   * @returns Promise resolving to the created event's document ID
   * 
   * @example
   * ```typescript
   * const eventId = await activityEventService.emitDeletion({
   *   domain: 'workflow',
   *   event_type: 'employee.removed',
   *   actor: { user_id: 'user123', user_name: 'Admin User' },
   *   subject: { resource_type: 'employee', resource_id: 'emp123', resource_name: 'John Doe' },
   *   previous_state: { role: 'Manager', team: 'Marketing' },
   *   metadata: {
   *     affected_user_ids: ['emp123', 'user123'],
   *     affected_workflow_ids: [],
   *     affected_employee_ids: ['emp123'],
   *     tags: ['employee', 'removal'],
   *     visibility: 'division'
   *   }
   * });
   * ```
   */
  async emitDeletion(input: DeleteEventInput): Promise<string> {
    // Build change object, filtering out undefined fields
    const change: any = {
      action: 'deleted' as ActionType,
    };
    
    // Only include previous_state if it is defined
    if (input.previous_state !== undefined) {
      change.previous_state = input.previous_state;
    }
    
    return this.emit({
      ...input,
      change,
      context: input.context || {},
    });
  }

  /**
   * Convenience method for emitting comment events
   * 
   * This method simplifies emitting comment/feedback events on resources.
   * 
   * @param input - Comment event input data
   * @returns Promise resolving to the created event's document ID
   * 
   * @example
   * ```typescript
   * const eventId = await activityEventService.emitComment({
   *   domain: 'workflow',
   *   event_type: 'workflow_spot.commented',
   *   actor: { user_id: 'user123', user_name: 'Jane Smith' },
   *   subject: { resource_type: 'workflow_spot', resource_id: 'spot123', resource_name: 'Spot Name' },
   *   message: 'This looks good, approved!',
   *   metadata: {
   *     affected_user_ids: ['user123', 'user456'],
   *     affected_workflow_ids: ['wf123'],
   *     affected_employee_ids: [],
   *     tags: ['comment', 'workflow_spot'],
   *     visibility: 'team'
   *   }
   * });
   * ```
   */
  async emitComment(input: CommentEventInput): Promise<string> {
    return this.emit({
      ...input,
      change: {
        action: 'submitted' as ActionType,
      },
      context: input.context || {},
    });
  }

  /**
   * Emit multiple related events as a batch
   * 
   * This method allows emitting multiple related events together, such as
   * when a single user action triggers multiple changes. All events in the
   * batch will share a common batch_id for correlation.
   * 
   * Note: This is a stub implementation for Phase 1. Future phases will
   * optimize this with Firestore batch writes for better performance.
   * 
   * @param input - Batch emit input with multiple events
   * @returns Promise resolving to an array of created event IDs
   * 
   * @example
   * ```typescript
   * const eventIds = await activityEventService.emitBatch({
   *   events: [
   *     {
   *       domain: 'workflow',
   *       event_type: 'program.created',
   *       actor: { user_id: 'user123', user_name: 'John Doe' },
   *       subject: { resource_type: 'program', resource_id: 'prog123', resource_name: 'New Program' },
   *       change: { action: 'created' },
   *       context: {},
   *       metadata: { affected_user_ids: ['user123'], affected_workflow_ids: [], affected_employee_ids: [], tags: ['program'], visibility: 'division' }
   *     },
   *     {
   *       domain: 'workflow',
   *       event_type: 'team.created',
   *       actor: { user_id: 'user123', user_name: 'John Doe' },
   *       subject: { resource_type: 'team', resource_id: 'team123', resource_name: 'New Team' },
   *       change: { action: 'created' },
   *       context: { program_id: 'prog123', program_name: 'New Program' },
   *       metadata: { affected_user_ids: ['user123'], affected_workflow_ids: [], affected_employee_ids: [], tags: ['team'], visibility: 'division' }
   *     }
   *   ]
   * });
   * ```
   */
  async emitBatch(input: BatchEmitInput): Promise<string[]> {
    try {
      // Generate a batch ID if not provided
      const batchId = input.batch_id || `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // For Phase 1, emit events sequentially
      // Future phases will use Firestore batch writes for better performance
      const eventIds: string[] = [];

      for (let i = 0; i < input.events.length; i++) {
        const event = input.events[i];
        const eventWithBatch: EmitActivityEventInput = {
          ...event,
          batch_id: batchId,
          is_batch_parent: i === 0, // First event is the batch parent
        };

        const eventId = await this.emit(eventWithBatch);
        eventIds.push(eventId);
      }

      return eventIds;
    } catch (error) {
      throw new Error(
        `Failed to emit batch events: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }
}

/**
 * Singleton instance of ActivityEventService
 * 
 * Import and use this instance throughout the application to emit activity events
 * across all domains.
 * 
 * @example
 * ```typescript
 * import { activityEventService } from '@/shared/services/activityEventService';
 * 
 * await activityEventService.emitCreation({
 *   domain: 'workflow',
 *   event_type: 'workflow_spot.created',
 *   actor: { user_id: userId, user_name: userName },
 *   subject: { resource_type: 'workflow_spot', resource_id: spotId, resource_name: spotName },
 *   metadata: { ... }
 * });
 * ```
 */
export const activityEventService = new ActivityEventService();
